import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CONFIG, { BaseURLType } from "../navigation/Configuration";
import { ClientType, UserRole, UserType } from "../common/entities/profile";
import {
  fetchClientsResilient,
  reportClientListAnomaly,
} from "../common/network/clientList";
import {
  getAllIntegrations,
  GetBiomarkers,
  GetBiometricQuestionnaire,
  getCustomerIngredient,
  GetCustomerPersonalInfo,
  getCustomerSettings,
  getNutritionQuestionnaire,
  getPlatformData,
  getUserInfo,
  settings,
  getLogicCategoriesNoAuth,
  GetBiomarkersTextNoAuth,
  getUserLabPanels,
  getKeragonWorkflows,
  getConsultation,
} from "../common/network/Api";
import { KeragonWorkflow } from "../common/entities/keragon";
import { useDispatch } from "react-redux";
import { platformActions } from "../modules/platform/store/platform-slice";
import { serviceLogicBuilderActions } from "../modules/platform/store/service-logic-builder-slice";
import { answersActions } from "../modules/questionnaire/store/answers-slice";
import { normalizeNutritionV2 } from "../modules/platform/components/nutritionTab/nutritionV2/normalizeNutritionV2";
import {
  getFromLocalStorageCompressedWithExpiry,
  getFromLocalStorageWithExpiry,
  getSelectedAgentTypes,
  saveToLocalStorageCompressedWithExpiry,
  saveToLocalStorageWithExpiry,
} from "./helperFunctions";
import { SnackbarCloseReason } from "@mui/material";
import { useAuth } from "./AuthProvider";
import {
  BiomarkerGroupedData,
  BiomarkerItem,
  LocalStorageVariables,
} from "../common/entities/type";
import { dataPlatformServerObjectType } from "./dataPlatformReportTypes";
import {
  setCustomerSession,
  subscribeCustomerSession,
} from "../common/network/customerSession";

const AppStoreContext = createContext({});

// localStorage key written after a Keragon workflow mutation so other tabs
// (client workspaces) reload their list — see loadKeragonWorkflows.
const KERAGON_WORKFLOWS_SYNC_KEY = "keragon_workflows_changed";

/**
 * Client datasets that pages load on demand. Each maps 1:1 to a page or area —
 * see `ensureResource` for the loader behind each name.
 */
export type PlatformResourceName =
  | "report"
  | "biomarkers"
  | "integrations"
  | "biometricQuestionnaire"
  | "nutritionQuestionnaire"
  | "personalInfo"
  | "consultation";

export type PlatformResourceStatus = "idle" | "loading" | "ready" | "error";

export type LoadClientDataResult =
  | { ok: true }
  | {
      ok: false;
      error: "load_failed" | "questionnaire_incomplete";
      email?: string;
    };

/**
 * Buttons of the two-action ("warning") snackbar. Labels default to
 * Cancel/Confirm, so callers only pass them when the action deserves a more
 * specific verb (e.g. "Review later" / "Review scores"). `tone` picks the
 * snackbar's icon: an alert mark by default, a success check when the prompt
 * follows something that went right.
 */
export type SnackBarActions = {
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "alert" | "success";
};

type PropsType = {
  children: ReactNode;
};

export function AppStoreProvider({ children }: PropsType) {
  const [configuration, setConfiguration] = useState<BaseURLType | null>(null);
  const [isLoadingPlatformData, setIsLoadingPlatformData] =
    useState<boolean>(true);
  const [integrations, setIntegrations] = useState([]);
  const [openSnackBar, setOpenSnackBar] = React.useState(false);
  const [typeSnackBar, setTypeSnackBar] = React.useState("alert");
  const [snackBarMessage, setSnackBarMessage] = React.useState({
    title: "",
    message: "",
  });
  const [snackBarActions, setSnackBarActions] =
    React.useState<SnackBarActions>({});
  const [selectedClient, setSelectedClient] = useState<UserType | null>(null);

  // Shared affiliate client list — loaded once and consumed by both ClientsList
  // and the Breadcrumb selector, so GetCustomers fires a single time per screen.
  const [clients, setClients] = useState<ClientType[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState<boolean>(false);
  const [clientsError, setClientsError] = useState<boolean>(false);
  const [clientsTotalCount, setClientsTotalCount] = useState<number | null>(
    null
  );
  const clientsPromiseRef = useRef<Promise<ClientType[]> | null>(null);
  const clientsLoadedRef = useRef<boolean>(false);

  // Keragon EHR workflows (company-level, affiliate only). Loaded lazily by
  // whoever needs them first — the Settings card and the Health overview's
  // "Add results" menu run in different browser tabs, so neither shell can
  // preload for the other. Same dedupe/cache shape as loadClients.
  const [keragonWorkflows, setKeragonWorkflows] = useState<KeragonWorkflow[]>(
    []
  );
  const [isLoadingKeragonWorkflows, setIsLoadingKeragonWorkflows] =
    useState<boolean>(false);
  const keragonWorkflowsDataRef = useRef<KeragonWorkflow[]>([]);
  const keragonWorkflowsPromiseRef = useRef<Promise<KeragonWorkflow[]> | null>(
    null
  );
  const keragonWorkflowsLoadedRef = useRef<boolean>(false);
  const clientsDataRef = useRef<ClientType[]>([]);

  const dispatch = useDispatch();

  const { AuthSetUser } = useAuth();

  const saveCostumer = async (newUser: any) => {
    setSelectedClient(newUser);
    setCustomerSession(newUser);
  };

  // Clear the selected client's React state without touching the tab's
  // customer session. Used to bail out of a cross-client context (e.g. after
  // authorizing an order for a different client) so the affiliate reselects
  // instead of seeing mixed data.
  const clearSelectedClient = () => {
    setSelectedClient(null);
  };

  // Keep `selectedClient`'s token in step with the tab's customer session.
  //
  // The 401 interceptor (AppApiManager) refreshes the customer token from
  // outside React, so without this the interceptor's retries would use the
  // fresh token while `ASSelectedClient` — the copy `useElevenLabsWidget`
  // sends the agent as `secret__token` — kept serving the expired one.
  //
  // Only the token is adopted, and only for the client already selected: a
  // session write for a DIFFERENT client means this tab is switching clients,
  // which is `saveCostumer`'s job, not ours. Returning `prev` unchanged keeps
  // the same object reference, so React bails out and `saveCostumer`'s own
  // `setCustomerSession` call cannot loop back into a re-render.
  useEffect(() => {
    return subscribeCustomerSession((session) => {
      setSelectedClient((prev) => {
        if (!prev || !session) return prev;
        if (String(prev.id) !== String(session.id)) return prev;
        if (prev.token === session.token) return prev;
        return { ...prev, token: session.token };
      });
    });
  }, []);

  //Snackbar functions
  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSnackBar(false);
    setSnackBarMessage({ title: "", message: "" });
    setSnackBarActions({});
  };

  const getEnvFromDomain = (): string => {
    const hostname = window.location.hostname;

    switch (hostname) {
      case "welcome.mypnoe.com":
      case "welcome-staging.mypnoe.com":
        return "prod";
      case "welcome-dev.mypnoe.com":
        return "dev";
      case "localhost":
      default:
        return "dev";
    }
  };

  const getEnv = async () => {
    const result = getEnvFromDomain();

    switch (result) {
      case "dev":
        setConfiguration(CONFIG.DEV);
        break;
      case "prod":
        setConfiguration(CONFIG.PROD);
        break;
      case "local":
        setConfiguration(CONFIG.LOCAL);
        break;
      default:
        setConfiguration(CONFIG.PROD);
    }
  };

  const getIntegrationData = async (role: number) => {
    let data = await getAllIntegrations(role);
    setIntegrations(data);
    return data;
  };

  const isIntegrationActive = (): boolean => {
    // Return false if integrations is empty
    if (integrations.length === 0) return false;

    // Check if any integration is connected
    return integrations.some(
      (item: { provider: string; is_connected: boolean }) => item.is_connected
    );
  };

  const GetUserInfo = async (tempUser: UserType) => {
    let temporaryUser: UserType = tempUser;

    let getUserData = await getUserInfo();
    // Whether a saved payment method exists (1) or not (0). Applies to any role,
    // so map it before the role branch. Left undefined if the response omits it.
    temporaryUser.paymentMethod = getUserData.paymentMethod;
    if (temporaryUser.role === UserRole.Customer) {
      temporaryUser.role = getUserData.role;
      temporaryUser.lastname = getUserData.lastName;
      temporaryUser.firstname = getUserData.firstName;
      temporaryUser.email = getUserData.email;
      temporaryUser.pnoe_user_id = getUserData.id;
      temporaryUser.origin = getUserData.origin;
      temporaryUser.isImport = getUserData.isImport;
      //TODO: add custom Share
    } else {
      const agentTypes = getSelectedAgentTypes(getUserData.agentType);
      const agentType = agentTypes.includes("Dashboard")
        ? "dashboard"
        : undefined;
      temporaryUser.name = getUserData.name;
      temporaryUser.email = getUserData.email;
      temporaryUser.role = getUserData.role;
      temporaryUser.locations = getUserData.locations;
      temporaryUser.agentType = agentType;
      temporaryUser.unitType = getUserData.unitType;
      //TODO: add custom Share
      // Coerce to a boolean so the key is always persisted to localStorage,
      // even if a future GetUser response omits it.
      temporaryUser.InBodyConnected = Boolean(getUserData.inBodyConnected);
    }

    return temporaryUser;
  };

  const getUserSettings = async (tempUser: UserType) => {
    let temporaryUser = tempUser;

    let settingsData = await settings();
    temporaryUser.biometrics_questionnaire_completed =
      settingsData?.biometrics_questionnaire_completed;
    temporaryUser.lifestyle_questionnaire_completed =
      settingsData?.lifestyle_questionnaire_completed;
    temporaryUser.has_lifestyle_questionnaire_access =
      settingsData?.has_lifestyle_questionnaire_access;
    temporaryUser.nutrition_questionnaire_completed =
      settingsData?.nutrition_questionnaire_completed;
    temporaryUser.has_active_plan = settingsData?.has_active_plan;
    temporaryUser.has_advanced_app_access =
      settingsData?.has_advanced_app_access;

    await AuthSetUser(temporaryUser);

    return temporaryUser;
  };

  // `silent` refreshes the platform data in the background without flipping the
  // global loading flag — callers (e.g. after a prescription save) that already
  // show their own loader use this so the whole client area isn't blanked by the
  // full-screen PageLoader on top of theirs.
  const getDataFromPlatform = async (
    id: string,
    role: number,
    silent: boolean = false
  ) => {
    try {
      if (!silent) setIsLoadingPlatformData(true);

      let dataPlatform: dataPlatformServerObjectType = await getPlatformData(
        id,
        role
      );

      dispatch(platformActions.updateDataPlatformServer(dataPlatform));
      if (!silent) setIsLoadingPlatformData(false);
    } catch (e: any) {
      if (!silent) setIsLoadingPlatformData(false);
      setSnackBarMessage({
        title: `Error in loading data from platform`,
        message: e.message,
      });
      setOpenSnackBar(true);
      dispatch(platformActions.clearPlatformState());
    }
  };

  const getLogicCategoriesList = async () => {
    const LOCAL_STORAGE_KEY = LocalStorageVariables.LOGIC_CATEGORIES_LIST;
    const EXPIRY_IN_MINUTES = 60;
    try {
      const cachedCategories = getFromLocalStorageWithExpiry(LOCAL_STORAGE_KEY);

      if (cachedCategories) {
        dispatch(platformActions.updateLogicCategories(cachedCategories));
        // Update service logic builder with Pnoe Metrics and Lab Work data
        dispatch(
          serviceLogicBuilderActions.updatePnoeMetrics(cachedCategories)
        );
        return;
      }
      const categories = await getLogicCategoriesNoAuth();

      // Save the fresh data to localStorage with an expiry
      saveToLocalStorageWithExpiry(
        LOCAL_STORAGE_KEY,
        categories,
        EXPIRY_IN_MINUTES
      );

      dispatch(platformActions.updateLogicCategories(categories));

      // Update service logic builder with Pnoe Metrics and Lab Work data
      dispatch(serviceLogicBuilderActions.updatePnoeMetrics(categories));
    } catch (e: any) {
      setSnackBarMessage({
        title: `Error in loading logic categories`,
        message: e.message,
      });
      setOpenSnackBar(true);
    }
  };

  // The biomarker catalogue: every biomarker's description (shown in the
  // biomarker card's tooltip, the biomarker panel's About tab and the system
  // details) plus the qualitative options the extractor and Keragon modals
  // read. It is 6 MB of JSON, so its cache is the compressed variant; and
  // nothing needs it before first paint — each consumer renders without it
  // until it lands — so the shells no longer wait for it (see
  // AffiliateShell / SubjectProvider).
  const getBiomarkerList = async () => {
    const LOCAL_STORAGE_KEY = LocalStorageVariables.BIOMARKERS_LIST;
    const EXPIRY_IN_MINUTES = 60;
    try {
      const cachedBiomarkerList =
        getFromLocalStorageCompressedWithExpiry(LOCAL_STORAGE_KEY);

      if (cachedBiomarkerList) {
        dispatch(platformActions.updateBiomarkerList(cachedBiomarkerList));
        return;
      }
      const biomarkersList = await GetBiomarkersTextNoAuth();

      dispatch(platformActions.updateBiomarkerList(biomarkersList));

      // Save the fresh data to localStorage with an expiry
      saveToLocalStorageCompressedWithExpiry(
        LOCAL_STORAGE_KEY,
        biomarkersList,
        EXPIRY_IN_MINUTES
      );
    } catch (e: any) {
      setSnackBarMessage({
        title: `Error in loading biomarkers text list`,
        message: e.message,
      });
      setOpenSnackBar(true);
    }
  };

  const getLabPanelsList = async () => {
    try {
      const response = await getUserLabPanels();
      dispatch(
        serviceLogicBuilderActions.setSavedLabPanels(
          response && Array.isArray(response) ? response : []
        )
      );
      return response;
    } catch (e: any) {
      setSnackBarMessage({
        title: `Error in loading lab panels`,
        message: e.message,
      });
      setOpenSnackBar(true);
      dispatch(serviceLogicBuilderActions.setSavedLabPanels([]));
      return [];
    }
  };

  const getClientBiomarkerTabDataHandler = async (id: string) => {
    try {
      const response = await GetBiomarkers(id);

      const groupByMetrics = (data: BiomarkerItem[]): BiomarkerGroupedData => {
        const result: BiomarkerGroupedData = {}; // Initialize the result object with proper typing

        data.forEach((item) => {
          item.metrics.forEach((metric) => {
            // If the metric group doesn't exist, create it
            if (!result[metric]) {
              result[metric] = [];
            }
            // Push the item into the corresponding metric group
            result[metric].push(item);
          });
        });

        return result; // Return the result object
      };

      // Call the function
      const groupedData = groupByMetrics(response);

      dispatch(platformActions.updateBiomarkerData(groupedData));
    } catch (e) {
      setSnackBarMessage({
        title: "Error in getting data for biomarker tab",
        // @ts-ignore
        message: e.message,
      });
      setOpenSnackBar(true);
    }
  };

  const getClientBiometricTabDataHandler = async (role: number) => {
    try {
      const response = await GetBiometricQuestionnaire(role);

      const {
        customer_id,
        created_at,
        updated_at,
        resting_cals,
        edits,
        ...answerRest
      } = response;

      // `edits` can be absent, null or empty; only a pending (non-permanent)
      // last edit carries modified fields to surface.
      const lastEdit =
        Array.isArray(edits) && edits.length > 0
          ? edits[edits.length - 1]
          : undefined;
      if (lastEdit && !lastEdit.is_permanent) {
        dispatch(
          answersActions.updateBiometricModifiedFields(lastEdit.modified_fields)
        );
        dispatch(
          answersActions.updateInitialBiometricModifiedFields(
            lastEdit.modified_fields
          )
        );
        dispatch(answersActions.updateInitialBiometricAnswers(lastEdit.old));
      } else {
        dispatch(answersActions.updateBiometricModifiedFields([]));
        dispatch(answersActions.updateInitialBiometricModifiedFields([]));
        dispatch(answersActions.updateInitialBiometricAnswers(answerRest));
      }
      dispatch(answersActions.updateBiometricAnswers(answerRest));
      dispatch(answersActions.updateOldBiometricAnswers(answerRest));
    } catch (e) {
      setSnackBarMessage({
        title: "Error in getting data for biometric tab",
        // @ts-ignore
        message: e.message,
      });
      setOpenSnackBar(true);
    }
  };

  const getDataForNutritionQuestionnaire = async (role: number) => {
    // Never reject: this runs inside loadClientData's Promise.all, and a
    // missing questionnaire (404) must not block the rest of the client load.
    try {
      const response = await getNutritionQuestionnaire(role);

      const ingredientNames = (
        await Promise.all(
          (response.preferred_key_ingredient_ids ?? []).map((id: string) =>
            getCustomerIngredient(id, role)
              .then((ing: { name?: string }) =>
                ing?.name
                  ? ing.name.charAt(0).toUpperCase() + ing.name.slice(1)
                  : null
              )
              .catch(() => null)
          )
        )
      ).filter(Boolean) as string[];

      dispatch(
        answersActions.updateNutritionAnswers(
          normalizeNutritionV2(response, ingredientNames)
        )
      );
    } catch (e) {
      dispatch(answersActions.updateNutritionAnswers(null));
    }
  };

  const getCustomerPersonalInfoHandler = async (
    id: string,
    options?: { deferClientSelection?: boolean }
  ) => {
    const deferSelection = options?.deferClientSelection ?? false;
    try {
      dispatch(answersActions.clearState());
      const selectedUser = await GetCustomerPersonalInfo(id);
      let tempUser: UserType = {
        role: 1,
        id: selectedUser.id,
        token: selectedUser.id_token,
        refresh_token: selectedUser.refresh_token,
        identity: selectedUser.identity,
        email: selectedUser.email,
        lastName: selectedUser.lastName,
        firstName: selectedUser.firstName,
        // TODO: add customShare
        // Customer's InBody profile code (backend casing may vary); exposed here
        // so the biomarker InBody sync modal can prefill it without re-fetching.
        InBodyAccount: selectedUser.inBodyAccount ?? selectedUser.InBodyAccount,
      };

      if (deferSelection) {
        // Save to the tab's customer session only (for API auth), skip React state update
        setCustomerSession(tempUser);
      } else {
        await saveCostumer(tempUser);
      }

      const tempUserPersonalInfo = {
        email: selectedUser.email,
        lastName: selectedUser.lastName,
        firstName: selectedUser.firstName,
        phone: selectedUser.phone,
        countryCode: selectedUser.countryCode,
        countryID: selectedUser.countryID,
        countryName: selectedUser.countryName,
        stateID: selectedUser.stateID,
        stateName: selectedUser.stateName,
        stateCode: selectedUser.stateCode,
        address: selectedUser.address,
        city: selectedUser.city,
        zip: selectedUser.zip,
        report: selectedUser.report,
        workout: selectedUser.workout,
        nutrition: selectedUser.nutrition,
      };

      let settingsData = await getCustomerSettings();
      tempUser.biometrics_questionnaire_completed =
        settingsData?.biometrics_questionnaire_completed;
      tempUser.lifestyle_questionnaire_completed =
        settingsData?.lifestyle_questionnaire_completed;
      tempUser.has_lifestyle_questionnaire_access =
        settingsData?.has_lifestyle_questionnaire_access;
      tempUser.nutrition_questionnaire_completed =
        settingsData?.nutrition_questionnaire_completed;
      tempUser.has_active_plan = settingsData?.has_active_plan;
      tempUser.has_advanced_app_access = settingsData?.has_advanced_app_access;

      if (deferSelection) {
        setCustomerSession(tempUser);
      } else {
        await saveCostumer(tempUser);
      }

      dispatch(
        answersActions.updatePersonalInfoInitialAnswers(tempUserPersonalInfo)
      );
      dispatch(answersActions.updatePersonalInfoAnswers(tempUserPersonalInfo));
      return tempUser;
    } catch (e) {
      setSnackBarMessage({
        title: "No costumer data available",
        // @ts-ignore
        message: e.message,
      });
      setOpenSnackBar(true);
    }
  };

  /**
   * The customer reading their own profile.
   *
   * Deliberately not `getCustomerPersonalInfoHandler`: that one exists to open a
   * client in an affiliate's tab, so it also clears the answers slice, mints a
   * customer token and sets the selected client. A customer is already
   * themselves — none of that applies, and setting a "selected client" here
   * would give a role-1 session affiliate state it should never have. Same
   * endpoint, same fields into the same two actions, nothing else.
   */
  const getSelfPersonalInfo = async (id: string) => {
    const profile = await GetCustomerPersonalInfo(id);
    if (!profile) return;

    const personalInfo = {
      email: profile.email,
      lastName: profile.lastName,
      firstName: profile.firstName,
      phone: profile.phone,
      countryCode: profile.countryCode,
      countryID: profile.countryID,
      countryName: profile.countryName,
      stateID: profile.stateID,
      stateName: profile.stateName,
      stateCode: profile.stateCode,
      address: profile.address,
      city: profile.city,
      zip: profile.zip,
      report: profile.report,
      workout: profile.workout,
      nutrition: profile.nutrition,
      // The client workspace carries this on the selected client; self mode has
      // no selected client, so it travels with the rest of the profile instead.
      inBodyAccount: profile.inBodyAccount ?? profile.InBodyAccount ?? "",
    };

    dispatch(answersActions.updatePersonalInfoInitialAnswers(personalInfo));
    dispatch(answersActions.updatePersonalInfoAnswers(personalInfo));
  };

  const loadClientData = async (
    clientId: string,
    role: number
  ): Promise<LoadClientDataResult> => {
    const response = await getCustomerPersonalInfoHandler(clientId, {
      deferClientSelection: true,
    });

    if (response === undefined) {
      saveCostumer(null);
      setIsLoadingPlatformData(false);
      return { ok: false, error: "load_failed" };
    }

    if (!response.biometrics_questionnaire_completed) {
      saveCostumer(null);
      setIsLoadingPlatformData(false);
      return {
        ok: false,
        error: "questionnaire_incomplete",
        email: response.email,
      };
    }

    // Identity only. The report, biomarkers, integrations and the two
    // questionnaires used to be awaited here, which meant every page in the
    // workspace paid for the slowest of them (GetReportv2, ~8s) even when it
    // read none of them. Each page now declares what it needs via
    // `usePlatformResource`, which dedupes and caches per client.
    saveCostumer(response);
    setIsLoadingPlatformData(false);

    return { ok: true };
  };

  // Single shared loader for the affiliate client list. De-dupes concurrent
  // callers (ClientsList + Breadcrumb) via an in-flight promise, retries an empty
  // response once with a cache-buster, and throws on an inconsistent empty so
  // callers can show an error/retry state instead of a false "No clients found".
  // Stable identity (empty deps: only setState/refs/module fns are referenced).
  const loadClients = useCallback(
    async (options?: { force?: boolean }): Promise<ClientType[]> => {
      if (!options?.force && clientsLoadedRef.current) {
        return clientsDataRef.current;
      }
      if (clientsPromiseRef.current) {
        return clientsPromiseRef.current;
      }

      const run = (async () => {
        setIsLoadingClients(true);
        setClientsError(false);
        try {
          const { list, totalCount } = await fetchClientsResilient();
          clientsDataRef.current = list;
          clientsLoadedRef.current = true;
          setClients(list);
          setClientsTotalCount(totalCount);
          return list;
        } catch (e: any) {
          if (!e?.__reported) {
            // A 401 here is a rejected session, not the empty-list anomaly — tag
            // it separately so it doesn't muddy the investigation. The value must
            // not contain "auth": Sentry's default data scrubbing redacted the
            // previous `auth-expired` to `[Filtered]`, hiding the one field that
            // tells the two failure modes apart.
            reportClientListAnomaly({
              anomalyType:
                e?.response?.status === 401 ? "session-rejected" : "error",
              status: e?.response?.status ?? null,
              error: String(e?.message ?? e),
            });
          }
          clientsLoadedRef.current = false;
          setClientsError(true);
          throw e;
        } finally {
          setIsLoadingClients(false);
          clientsPromiseRef.current = null;
        }
      })();

      clientsPromiseRef.current = run;
      return run;
    },
    []
  );

  // ─── Per-page client resources ─────────────────────────────────────────────
  //
  // Each dataset is fetched by the page that needs it rather than front-loaded
  // by `loadClientData`. Entries are keyed by `${resource}:${subjectId}` so a
  // tab showing a different client never reads a cache that isn't theirs, and
  // changing `?id=` naturally misses.
  //
  // Same shape as `loadClients` above: a ref for the in-flight promise (dedupes
  // concurrent callers — several components on one page ask for the same
  // resource), a ref for "already loaded" (so Profile → Score → Profile does not
  // refetch), and React state for the status the UI renders from.
  //
  // The loaders are read through a ref because they close over the current
  // render's `dispatch`/setters, while `ensureResource` must keep a stable
  // identity — it is a `useEffect` dependency in every consuming page.
  const resourcePromisesRef = useRef<Record<string, Promise<void> | null>>({});
  const resourceLoadedRef = useRef<Record<string, boolean>>({});
  const [resourceStatus, setResourceStatus] = useState<
    Record<string, PlatformResourceStatus>
  >({});

  // Raw `GetConsultation` payloads per subject id (the Health overview reads
  // its body composition and energy-training zones). Not in redux: nothing
  // else mutates it, and keying by subject keeps a client switch from showing
  // the previous client's zones.
  const [consultationBySubject, setConsultationBySubject] = useState<
    Record<string, any>
  >({});
  const getConsultationData = useCallback(
    (subjectId: string | null | undefined) =>
      subjectId ? consultationBySubject[subjectId] ?? null : null,
    [consultationBySubject]
  );

  const loadersRef = useRef<
    Record<PlatformResourceName, (id: string, role: number) => Promise<any>>
  >({} as any);
  loadersRef.current = {
    // `silent` keeps this off `isLoadingPlatformData` — flipping that flag would
    // put the full-page loader back and undo the whole point of this change.
    report: (id, role) => getDataFromPlatform(id, role, true),
    biomarkers: (id) => getClientBiomarkerTabDataHandler(id),
    integrations: (_id, role) => getIntegrationData(role),
    biometricQuestionnaire: (_id, role) => getClientBiometricTabDataHandler(role),
    nutritionQuestionnaire: (_id, role) => getDataForNutritionQuestionnaire(role),
    personalInfo: (id) => getSelfPersonalInfo(id),
    consultation: async (id) => {
      const resp = await getConsultation(id);
      setConsultationBySubject((prev) => ({ ...prev, [id]: resp }));
    },
  };

  // Stable identity (empty deps: only setState/refs/module fns referenced).
  // `force` bypasses the cache — the Settings card passes it after every
  // mutation so the list (and the menu's locked state) reflects the server.
  // `broadcast` additionally pings the other browser tabs (the client
  // workspaces) through a localStorage key so they reload too; the listener
  // below reloads WITHOUT broadcasting, so tabs never ping each other in a loop.
  const loadKeragonWorkflows = useCallback(
    async (options?: {
      force?: boolean;
      broadcast?: boolean;
    }): Promise<KeragonWorkflow[]> => {
      if (!options?.force && keragonWorkflowsLoadedRef.current) {
        return keragonWorkflowsDataRef.current;
      }
      if (keragonWorkflowsPromiseRef.current) {
        return keragonWorkflowsPromiseRef.current;
      }
      const run = (async () => {
        setIsLoadingKeragonWorkflows(true);
        try {
          const list = await getKeragonWorkflows();
          keragonWorkflowsDataRef.current = list;
          keragonWorkflowsLoadedRef.current = true;
          setKeragonWorkflows(list);
          if (options?.broadcast) {
            try {
              localStorage.setItem(KERAGON_WORKFLOWS_SYNC_KEY, String(Date.now()));
            } catch {
              // Storage unavailable — other tabs fall back to refetching on open.
            }
          }
          return list;
        } catch (e) {
          console.error("Error loading Keragon workflows:", e);
          return keragonWorkflowsDataRef.current;
        } finally {
          setIsLoadingKeragonWorkflows(false);
          keragonWorkflowsPromiseRef.current = null;
        }
      })();
      keragonWorkflowsPromiseRef.current = run;
      return run;
    },
    []
  );

  // Another tab (Settings) changed the workflows: refresh this tab's copy so
  // the Health overview's "Sync EHR data" option unlocks without a reload.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === KERAGON_WORKFLOWS_SYNC_KEY && event.newValue) {
        loadKeragonWorkflows({ force: true });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [loadKeragonWorkflows]);

  const ensureResource = useCallback(
    async (
      name: PlatformResourceName,
      subjectId: string | null | undefined,
      role: number,
      options?: { force?: boolean; silent?: boolean }
    ): Promise<void> => {
      if (!subjectId) return;
      const key = `${name}:${subjectId}`;

      if (!options?.force && resourceLoadedRef.current[key]) return;

      const inFlight = resourcePromisesRef.current[key];
      if (inFlight) return inFlight;

      const run = (async () => {
        if (!options?.silent) {
          setResourceStatus((prev) => ({ ...prev, [key]: "loading" }));
        }
        try {
          await loadersRef.current[name](subjectId, role);
          resourceLoadedRef.current[key] = true;
          setResourceStatus((prev) => ({ ...prev, [key]: "ready" }));
        } catch (e) {
          // The individual loaders already surface their own snackbars; record
          // the failure so pages can show a retry instead of an empty state.
          resourceLoadedRef.current[key] = false;
          setResourceStatus((prev) => ({ ...prev, [key]: "error" }));
        } finally {
          resourcePromisesRef.current[key] = null;
        }
      })();

      resourcePromisesRef.current[key] = run;
      return run;
    },
    []
  );

  /** Re-fetch after a mutation, bypassing the cache. */
  const refreshResource = useCallback(
    (
      name: PlatformResourceName,
      subjectId: string | null | undefined,
      role: number,
      options?: { silent?: boolean }
    ) => ensureResource(name, subjectId, role, { ...options, force: true }),
    [ensureResource]
  );

  const getResourceStatus = useCallback(
    (
      name: PlatformResourceName,
      subjectId: string | null | undefined
    ): PlatformResourceStatus =>
      subjectId ? resourceStatus[`${name}:${subjectId}`] ?? "idle" : "idle",
    [resourceStatus]
  );

  useEffect(() => {
    if (configuration === null) {
      getEnv();
    }
  }, [configuration]);

  const value = useMemo(
    () => ({
      ASConfiguration: configuration?.ID ? configuration.ID : "",
      ASisLoadingPlatformData: isLoadingPlatformData,
      ASSetIsLoadingPlatformData: setIsLoadingPlatformData,
      ASGetUserSettings: getUserSettings,
      ASGetUserInfo: GetUserInfo,
      ASIntegrations: integrations,
      ASIsIntegrationActive: isIntegrationActive,
      ASGetIntegrationData: getIntegrationData,
      ASGetDataFromPlatform: getDataFromPlatform,
      ASGetDataForBiometricTab: getClientBiometricTabDataHandler,
      ASGetDataForNutritionTab: getDataForNutritionQuestionnaire,
      ASGetClientBiomarkerTabDataHandler: getClientBiomarkerTabDataHandler,
      ASGetCustomerPersonalInfo: getCustomerPersonalInfoHandler,
      ASLoadClientData: loadClientData,
      ASGetBiomarkersList: getBiomarkerList,
      ASGetLogicCategoriesList: getLogicCategoriesList,
      ASGetLabPanelsList: getLabPanelsList,
      ASSelectedClient: selectedClient,
      ASSetSelectedClient: saveCostumer,
      ASClearSelectedClient: clearSelectedClient,
      // Shared affiliate client list
      ASClients: clients,
      ASIsLoadingClients: isLoadingClients,
      ASClientsError: clientsError,
      ASClientsTotalCount: clientsTotalCount,
      ASLoadClients: loadClients,
      // Keragon EHR workflows (see loadKeragonWorkflows)
      ASKeragonWorkflows: keragonWorkflows,
      ASIsLoadingKeragonWorkflows: isLoadingKeragonWorkflows,
      ASLoadKeragonWorkflows: loadKeragonWorkflows,
      // Per-page client resources (see ensureResource)
      ASEnsureResource: ensureResource,
      ASRefreshResource: refreshResource,
      ASResourceStatus: getResourceStatus,
      ASConsultation: getConsultationData,
      //Snackbar functions
      ASOpenSnackBar: openSnackBar,
      ASSetOpenSnackBar: setOpenSnackBar,
      ASSnackBarMessage: snackBarMessage,
      ASSetSnackBarMessage: setSnackBarMessage,
      ASTypeSnackBar: typeSnackBar,
      ASSetTypeSnackBar: setTypeSnackBar,
      ASHandleCloseSnackBar: handleClose,
      ASSnackBarActions: snackBarActions,
      ASSetSnackBarActions: setSnackBarActions,
    }),
    [
      configuration,
      isLoadingPlatformData,
      integrations,
      selectedClient,
      clients,
      isLoadingClients,
      clientsError,
      clientsTotalCount,
      loadClients,
      keragonWorkflows,
      isLoadingKeragonWorkflows,
      loadKeragonWorkflows,
      ensureResource,
      refreshResource,
      getResourceStatus,
      getConsultationData,
      openSnackBar,
      snackBarMessage,
      typeSnackBar,
      snackBarActions,
    ]
  );

  return (
    <AppStoreContext.Provider value={value}>
      {children}
    </AppStoreContext.Provider>
  );
}

type ContextValueType = {
  ASConfiguration: string;
  ASisLoadingPlatformData: boolean;
  ASSetIsLoadingPlatformData: Function;
  ASGetUserSettings: Function;
  ASGetUserInfo: Function;
  ASIntegrations: [];
  ASIsIntegrationActive: Function;
  ASGetIntegrationData: Function;
  ASGetDataFromPlatform: Function;
  ASGetDataForBiometricTab: (role: number) => Promise<void>;
  ASGetDataForNutritionTab: Function;
  ASGetClientBiomarkerTabDataHandler: Function;
  ASGetCustomerPersonalInfo: Function;
  ASLoadClientData: (
    clientId: string,
    role: number
  ) => Promise<LoadClientDataResult>;
  ASGetBiomarkersList: Function;
  ASGetLogicCategoriesList: Function;
  ASGetLabPanelsList: Function;
  ASSelectedClient: UserType | null;
  ASSetSelectedClient: Function;
  ASClearSelectedClient: Function;
  // Shared affiliate client list
  ASClients: ClientType[];
  ASIsLoadingClients: boolean;
  ASClientsError: boolean;
  ASClientsTotalCount: number | null;
  ASLoadClients: (options?: { force?: boolean }) => Promise<ClientType[]>;
  // Keragon EHR workflows
  ASKeragonWorkflows: KeragonWorkflow[];
  ASIsLoadingKeragonWorkflows: boolean;
  ASLoadKeragonWorkflows: (options?: {
    force?: boolean;
    broadcast?: boolean;
  }) => Promise<KeragonWorkflow[]>;
  ASEnsureResource: (
    name: PlatformResourceName,
    subjectId: string | null | undefined,
    role: number,
    options?: { force?: boolean; silent?: boolean }
  ) => Promise<void>;
  ASRefreshResource: (
    name: PlatformResourceName,
    subjectId: string | null | undefined,
    role: number,
    options?: { silent?: boolean }
  ) => Promise<void>;
  ASResourceStatus: (
    name: PlatformResourceName,
    subjectId: string | null | undefined
  ) => PlatformResourceStatus;
  /** Raw `GetConsultation` payload of the "consultation" resource, or null. */
  ASConsultation: (subjectId: string | null | undefined) => any;
  //Snackbar functions
  ASOpenSnackBar: boolean;
  ASSetOpenSnackBar: (value: boolean) => void;
  ASSnackBarMessage: { title: string; message: string };
  ASSetSnackBarMessage: (value: { title: string; message: string }) => void;
  ASTypeSnackBar: string;
  ASSetTypeSnackBar: (value: string) => void;
  ASHandleCloseSnackBar: any;
  ASSnackBarActions: SnackBarActions;
  ASSetSnackBarActions: (value: SnackBarActions) => void;
};

export const useConfig = () => {
  const getEnvFromDomain = (): string => {
    const hostname = window.location.hostname;

    switch (hostname) {
      case "welcome.mypnoe.com":
      case "welcome-staging.mypnoe.com":
        return "prod";
      case "welcome-dev.mypnoe.com":
        return "dev";
      case "localhost":
      default:
        return "dev";
    }
  };

  const result = getEnvFromDomain();

  switch (result) {
    case "dev":
      return CONFIG.DEV;
    case "local":
      return CONFIG.LOCAL;
    default:
      return CONFIG.PROD;
  }
};

export function useAppStore() {
  const context = useContext(AppStoreContext);

  if (!context) {
    throw new Error("useAppStore must be used inside AppStoreProvider");
  }

  return context as ContextValueType;
}
